import Router from 'next/router';
import isEmpty from 'lodash/isEmpty';

import config from '../../../config';
import { toQueryString } from '../../../utils/fetch';
import routing from '../../../utils/routing';
import { SSO_FILTER_PARAMS } from '../../../utils/constants';

export const TYPES = {
  SET_HISTORY: 'router/SET_HISTORY',
  POP_HISTORY: 'router/POP_HISTORY',
};

export const PAGES = {
  BILLING_CARD: '/employer-center/billing/cards',
  BILLING: '/employer-center/billing',
  CAMPAIGN_ANALYTICS: '/employer-center/campaign-analytics',
  CODE_OF_CONDUCT: '/code-of-conduct',
  CONTACT_JOBCASE: 'mailto:help@jobcase.com',
  DASHBOARD: '/employer-center/dashboard',
  EMPLOYERS: '/employer-center',
  HIRING_EVENTS: '/employer-center/hiring-events',
  HOME: '/',
  HUBS: '/employer-center/hubs',
  LOGIN: '/login',
  LOGOUT: '/logout',
  MANAGE_BUDGETS: '/employer-center/manage-budgets',
  JOB_REPORTING: '/employer-center/job-reporting',
  NOTIFICATIONS: '/employer-center/notifications',
  PRIVACY_POLICY: '/privacy-policy',
  PROFILE: '/profile',
  PUBLISHER_AGREEMENT: '/about-us/jobcase-publisher-agreement/',
  SERVICES_AGREEMENT: '/jobcase-services-agreement',
  SETTINGS: '/employer-center/settings',
  SUB_ORGANIZATIONS: '/employer-center/settings/sub-organizations',
  TERMS_OF_USE: '/terms-of-use',
  USER_ROLES: '/employer-center/user-roles-permissions',
};

export const RAILS_PAGES = {
  PRAISE: '/praises',
  CONTACT_US: '/contact_us',
  LOGIN: '/login',
  SIGNUP: '/registration/new',
  OPTIMIZATION: '/streetsmarts/job_optimization_campaigns',
};

export const GLOBAL_ROUTES = {
  NOTIFICATIONS: '/employer-center/notifications',
};

export const routeTo = (url, isExternalUrl = false, query = null, openInNewTab = false) => {
  // This is done to filter out params we don't want to pass around such as odin or utm.
  const filteredParams = routing.filterParams(query);
  const completeUrl = `${url}${toQueryString(filteredParams)}`;
  if (openInNewTab) {
    window.open(completeUrl, '_blank');
  } else if (isExternalUrl) {
    window.location.href = completeUrl;
  } else if (typeof window !== 'undefined') {
    Router.push({
      pathname: url,
      query: filteredParams,
    });
  }
};

const getUrlWithQuery = (url, query) => {
  if (!url) return '';
  let urlWithQuery = url;
  if (!isEmpty(query)) {
    urlWithQuery += '?';
    const params = Object.keys(query);
    params.forEach((param, i) => {
      const value = query[param] || ''; // generates q=&l=02139
      urlWithQuery += `${i !== 0 ? '&' : ''}${param}=${encodeURIComponent(value)}`;
    });
  }
  return urlWithQuery;
};

// deprecated - routeTo handles dynamic redirect without page refresh
const routeToDynamic = (pattern, url, query) => {
  Router.push(getUrlWithQuery(pattern, query), getUrlWithQuery(url, query));
};

const queryStringToObject = (queryString) => {
  const queryObject = {};

  queryString.replace(/([^=&]+)=([^&]*)/g, (m, key, value) => {
    queryObject[decodeURIComponent(key)] = decodeURIComponent(value);
  });
  return queryObject;
};

/* This function is used to filter the query parameters of a string url
 * @param url is the url used
 * @param parameters is the array of regular expressions used to filter the query
*/
export const getFilteredUrl = (url, parameters) => {
  const urlNoParametersStr = url.split('?')[0];
  const urlParametersStr = url.split('?').length > 1 ? url.split('?')[1] : '';

  if (urlParametersStr) {
    const paramsObject = queryStringToObject(urlParametersStr);
    const filteredParams = routing.filterParams(paramsObject, parameters);
    const filteredParamsStr = encodeURIComponent(toQueryString(filteredParams));

    return `${urlNoParametersStr}${filteredParamsStr}`;
  }
  return urlNoParametersStr;
};

export const routeToLogin = (redirect) => {
  let filteredSSOUrl = getFilteredUrl(redirect || window.location.href, SSO_FILTER_PARAMS);
  if (!['staging', 'production'].includes(process.env.NODE_ENV)) {
    const paramSeparator = filteredSSOUrl.split('?')[1] ? '&' : '?';
    filteredSSOUrl = encodeURIComponent(`${filteredSSOUrl}${paramSeparator}redirect_uri=${filteredSSOUrl}`);
  }
  routeTo(`${config.jobcaseUrl}${RAILS_PAGES.LOGIN}`, true, { redirect_uri: filteredSSOUrl });
};

export const routeToSignup = (redirect) => {
  const filteredSSOUrl = getFilteredUrl(redirect || window.location.href, SSO_FILTER_PARAMS);
  routeTo(`${config.jobcaseUrl}${RAILS_PAGES.SIGNUP}`, true, { redirect_uri: filteredSSOUrl });
};

export const routeToContactUs = () => {
  routeTo(`${config.jobcaseUrl}${RAILS_PAGES.CONTACT_US}`, true);
};

export const routeToWFSOptimization = () => {
  routeTo(`${config.jobcaseUrl}${RAILS_PAGES.OPTIMIZATION}`, true, { employer_id: '1897bc5f-f610-40b5-a8cc-b58baffefdcc' });
};

export const routeToRetailOptimization = () => {
  routeTo(`${config.jobcaseUrl}${RAILS_PAGES.OPTIMIZATION}`, true, { employer_id: '30733d0d-64db-46ef-89fe-14b9b350eb5d' });
};

export const routeToPillpackOptimization = () => {
  routeTo(`${config.jobcaseUrl}${RAILS_PAGES.OPTIMIZATION}`, true, { employer_id: 'e77c99e5-b6ba-4a56-b30d-ae38ce0530bb' });
};

export const routeToMarketingSite = () => {
  routeTo(`${config.jobcaseUrl}/hire`, true);
};

export const routeToEmployerCenterLogin = (redirectUri) => {
  routeTo(`${config.jobcaseUrl}/login?redirect_uri=${redirectUri}`, true);
};

export const routeToGroups = () => `${config.jobcaseUrl}/groups`;

export const routeToVerification = (query = {}) => {
  routeTo('/organization-verification', false, query);
};

export const routeToProfile = () => routeTo(`${PAGES.PROFILE}`, true);

export const routeToProfileExpanded = (profile, isLoggedInUser, query = null) => {
  if (isLoggedInUser) {
    routeTo(`${PAGES.PROFILE}`, false, query);
  } else if (profile.username) {
    routeToDynamic(`${PAGES.PROFILE_USERNAME}/[username]`, `${PAGES.PROFILE_USERNAME}/${profile.username}`, query);
  } else {
    routeToDynamic(`${PAGES.PROFILE}/[id]`, `${PAGES.PROFILE}/${profile.key}`, query);
  }
};

export const getMyProfileHref = (query = null) => getUrlWithQuery(PAGES.PROFILE, query);
export const routeToMyProfile = (query = null) => routeTo(`${PAGES.PROFILE}`, false, query);

export const getProfileHref = profileKey => `${config.jobcaseUrl}${PAGES.PROFILE}/${profileKey}`;

export const getProfileHrefExpanded = profile => (profile.username
  ? `${PAGES.PROFILE_USERNAME}/${profile.username}`
  : `${PAGES.PROFILE}/${profile.key}`);

export const getUserFriendlyProfileHref = (profileKey, username) => (username
  ? `${config.jobcaseUrl}${PAGES.PROFILE_USERNAME}/${username}`
  : `${config.jobcaseUrl}${PAGES.PROFILE}/${profileKey}`);

export const routeToContent = (query) => {
  routeTo(`${PAGES.POSTS}`, false, query);
};

export const getContentHref = query => getUrlWithQuery(PAGES.POSTS, query);

export const routeToPrivacyPolicy = () => {
  routeTo(`${PAGES.PRIVACY_POLICY}`);
};

export const routeToHiringEvents = (query = {}) => {
  routeTo(`${PAGES.HIRING_EVENTS}`, false, query);
};

export const routeToHiringEventDetail = (id, query = {}, newTab = false) => {
  routeTo(`${PAGES.HIRING_EVENTS}/${id}`, false, query, newTab);
};

export const routeToHubDetail = (id, query = {}, newTab = false) => {
  routeTo(`${PAGES.HUBS}/${id}`, false, query, newTab);
};

export const routeToSubOrgDetail = (id, query = {}, newTab = false) => {
  routeTo(`${PAGES.SUB_ORGANIZATIONS}/${id}`, false, query, newTab);
};

export const routeToUserRoles = (query = {}) => {
  routeTo(`${PAGES.USER_ROLES}`, false, query);
};

export const routeToHubs = (query = {}) => {
  routeTo(`${PAGES.HUBS}`, false, query);
};

export const routeToBilling = (query = {}, tab = 'cards') => {
  routeTo(`${PAGES.BILLING}/${tab}`, false, query);
};

export const routeToSingleCardPage = (id, query = { tab: 'overview' }) => {
  routeTo(`${PAGES.BILLING}/cards/${id}`, false, query);
};

// The Jobcase Community was sunset (member app 2.0.0, 2026-09). Its Employer Center entry
// points were removed; the one place that still needs an exit (no Employer Center access at
// all) sends people to the Jobcase home page on the member site.
export const routeToJobcaseHome = () => {
  routeTo(`${config.jobcaseUrl}${PAGES.HOME}`, true);
};

export const routeToTermsOfUse = () => {
  routeTo(`${PAGES.TERMS_OF_USE}`);
};

export const routeToServicesAgreement = () => {
  routeTo(`${PAGES.SERVICES_AGREEMENT}`);
};

export const routeToCodeOfConduct = () => {
  routeTo(`${PAGES.CODE_OF_CONDUCT}`);
};

export const routeToSettings = (query = {}) => {
  routeTo(`${PAGES.SETTINGS}`, false, query);
};

export const routeToDashboard = (query = {}) => {
  routeTo(`${PAGES.DASHBOARD}`, false, query);
};

export const routeToNotifications = () => {
  routeTo(`${PAGES.NOTIFICATIONS}`);
};

export const routeToContactJobcase = () => {
  routeTo(`${PAGES.CONTACT_JOBCASE}`, true, null, true);
};

export const routeToLogout = () => {
  routeTo(`${PAGES.LOGOUT}`, true);
};

export const routeToEmployerCenter = () => {
  routeTo(`${PAGES.EMPLOYERS}`);
};

export const routeToEmployerCenterExternal = () => {
  routeTo(`${PAGES.EMPLOYERS}`, true);
};

export const routeToHiringEventPreview = (id) => {
  routeTo(`${config.jobcaseUrl}/events/${id}`, true, { preview_mode: true }, true);
};

export const routeToMemberHubView = (slug, query = null) => {
  routeTo(`${config.jobcaseUrl}/organizations/${slug}`, true, query, true);
};

// Dispatch this action. If router redux state is empty, redirect to forYou.
export const routePageBack = () => (_dispatch, getState) => {
  const { history } = getState().router;
  if (!isEmpty(history)) {
    Router.back();
  }
};

export const setHistory = page => (dispatch) => {
  dispatch({ type: TYPES.SET_HISTORY, payload: page });
};

export const popHistory = () => (dispatch) => {
  dispatch({ type: TYPES.POP_HISTORY });
};

// Helper to separate query from urls
export const getUrlAndQuery = (url) => {
  const decodedUrl = decodeURIComponent(url);
  const urlNoParametersStr = decodedUrl.split('?')[0];
  const urlParametersStr = decodedUrl.split('?').length > 1 ? decodedUrl.split('?')[1] : '';
  const querys = {};
  if (!isEmpty(urlParametersStr)) {
    urlParametersStr.split('&').forEach((pair) => { querys[pair.split('=')[0]] = unescape((pair.split('=')[1] || '').replace(/\+/g, '%20')); });
  }
  return [urlNoParametersStr, querys];
};

export const routeToOnboarding = (query) => {
  routeTo(`${PAGES.ONBOARDING}`, false, query);
};

export const routeToEmployerCenterToOrganization = (organizationId) => {
  routeTo(`${PAGES.EMPLOYERS}`, true, {
    employer: organizationId,
  }, false);
};

export const routeToManageBudgets = (query = {}) => {
  routeTo(`${PAGES.MANAGE_BUDGETS}`, false, query);
};

export const routeToJobReporting = (query = {}) => {
  routeTo(`${PAGES.JOB_REPORTING}`, false, query);
};

export default {
  PAGES,
  GLOBAL_ROUTES,
  routePageBack,
  routeToLogin,
  routeToProfile,
  routeToProfileExpanded,
  routeToMyProfile,
  routeToBilling,
  routeToSingleCardPage,
  getMyProfileHref,
  getProfileHref,
  getProfileHrefExpanded,
  getUserFriendlyProfileHref,
  routeToSignup,
  routeToContactUs,
  routeToWFSOptimization,
  routeToRetailOptimization,
  routeToPillpackOptimization,
  routeToMarketingSite,
  routeToEmployerCenterLogin,
  setHistory,
  popHistory,
  routeToContent,
  getContentHref,
  getFilteredUrl,
  routeToPrivacyPolicy,
  routeToTermsOfUse,
  routeToServicesAgreement,
  routeToCodeOfConduct,
  routeToVerification,
  routeToManageBudgets,
  routeToJobReporting,
};
